import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';

export enum FundingSourceOptions {
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK_DEPOSIT = 'CHECK_DEPOSIT',
  COMMERCE_PLATFORM = 'COMMERCE_PLATFORM',
  PAYMENT_PLATFORM = 'PAYMENT_PLATFORM',
  CREDIT_LOAN = 'CREDIT_LOAN',
  OTHER = 'OTHER',
}

export const useFundingSourcesLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  // Define the options list
  const optionsList = [
    {
      value: FundingSourceOptions.BANK_TRANSFER,
      getLabel: () => t('organic.fundingSources.bankTransfer'),
      field: EddEmbeddedFields.EDD_FUNDING_BANK_TRANSFER,
      placeholder: t('organic.fundingSources.bankTransferPlaceholder'),
    },
    {
      value: FundingSourceOptions.CHECK_DEPOSIT,
      getLabel: () => t('organic.fundingSources.checkDeposit'),
      field: EddEmbeddedFields.EDD_FUNDING_CHECK_DEPOSIT,
      placeholder: t('organic.fundingSources.checkDepositPlaceholder'),
    },
    {
      value: FundingSourceOptions.COMMERCE_PLATFORM,
      getLabel: () => t('organic.fundingSources.commercePlatform'),
      field: EddEmbeddedFields.EDD_FUNDING_COMMERCE_PLATFORM,
      placeholder: t('organic.fundingSources.commercePlatformPlaceholder'),
    },
    {
      value: FundingSourceOptions.PAYMENT_PLATFORM,
      getLabel: () => t('organic.fundingSources.paymentPlatform'),
      field: EddEmbeddedFields.EDD_FUNDING_PAYMENT_PLATFORM,
      placeholder: t('organic.fundingSources.paymentPlatformPlaceholder'),
    },
    {
      value: FundingSourceOptions.CREDIT_LOAN,
      getLabel: () => t('organic.fundingSources.creditLoan'),
      field: EddEmbeddedFields.EDD_FUNDING_CREDIT_LOAN,
      placeholder: t('organic.fundingSources.creditLoanPlaceholder'),
    },
    {
      value: FundingSourceOptions.OTHER,
      getLabel: () => t('organic.fundingSources.other'),
      field: EddEmbeddedFields.EDD_FUNDING_OTHER,
      placeholder: t('organic.fundingSources.otherPlaceholder'),
    },
  ];

  // Create a single schema with all the validation logic
  const formSchema = z
    .object({
      [EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED]: z
        .array(z.string())
        .min(1, { message: t('organic.fundingSources.validation.selectAtLeastOne') }),
      [EddEmbeddedFields.EDD_FUNDING_BANK_TRANSFER]: z.string().optional(),
      [EddEmbeddedFields.EDD_FUNDING_CHECK_DEPOSIT]: z.string().optional(),
      [EddEmbeddedFields.EDD_FUNDING_COMMERCE_PLATFORM]: z.string().optional(),
      [EddEmbeddedFields.EDD_FUNDING_PAYMENT_PLATFORM]: z.string().optional(),
      [EddEmbeddedFields.EDD_FUNDING_CREDIT_LOAN]: z.string().optional(),
      [EddEmbeddedFields.EDD_FUNDING_OTHER]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // Get selected options
      const selectedOptions = data[EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED] as string[];

      // Define validation for a single field
      const validateField = (field: EddEmbeddedFields, option: string) => {
        if (selectedOptions.includes(option)) {
          // Use a specific type instead of Record<string, any>
          const value = data[field as keyof typeof data] as string | undefined;
          if (!value || value.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.fundingSources.validation.enterValue'),
              path: [field],
            });
          } else if (value.length < 4) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.fundingSources.validation.minLength'),
              path: [field],
            });
          } else if (value.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('organic.fundingSources.validation.maxLength'),
              path: [field],
            });
          }
        }
      };

      // Validate each field if its option is selected
      validateField(
        EddEmbeddedFields.EDD_FUNDING_BANK_TRANSFER,
        FundingSourceOptions.BANK_TRANSFER
      );
      validateField(
        EddEmbeddedFields.EDD_FUNDING_CHECK_DEPOSIT,
        FundingSourceOptions.CHECK_DEPOSIT
      );
      validateField(
        EddEmbeddedFields.EDD_FUNDING_COMMERCE_PLATFORM,
        FundingSourceOptions.COMMERCE_PLATFORM
      );
      validateField(
        EddEmbeddedFields.EDD_FUNDING_PAYMENT_PLATFORM,
        FundingSourceOptions.PAYMENT_PLATFORM
      );
      validateField(EddEmbeddedFields.EDD_FUNDING_CREDIT_LOAN, FundingSourceOptions.CREDIT_LOAN);
      validateField(EddEmbeddedFields.EDD_FUNDING_OTHER, FundingSourceOptions.OTHER);
    });

  // Create form default values
  const formDefaultValues = {
    [EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED]: (() => {
      const value = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED);
      if (!Array.isArray(value)) return [];
      return value;
    })(),
    [EddEmbeddedFields.EDD_FUNDING_BANK_TRANSFER]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_BANK_TRANSFER) || '',
    [EddEmbeddedFields.EDD_FUNDING_CHECK_DEPOSIT]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_CHECK_DEPOSIT) || '',
    [EddEmbeddedFields.EDD_FUNDING_COMMERCE_PLATFORM]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_COMMERCE_PLATFORM) || '',
    [EddEmbeddedFields.EDD_FUNDING_PAYMENT_PLATFORM]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_PAYMENT_PLATFORM) || '',
    [EddEmbeddedFields.EDD_FUNDING_CREDIT_LOAN]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_CREDIT_LOAN) || '',
    [EddEmbeddedFields.EDD_FUNDING_OTHER]:
      getDefaultValueByQuestionId(EddEmbeddedFields.EDD_FUNDING_OTHER) || '',
  };

  // Initialize form with schema and default values
  const formMethods = useFormHook({
    schemaTitle: 'Funding Sources/Origins',
    formSchema,
    formDefaultValues,
  });

  // Handle form submission
  const handleSubmit = async () => {
    const selectedOptions = JSON.stringify(
      formMethods.getValues(EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED) as string[]
    );

    // Save the selected options
    saveEddQuestionToLocalStore({
      question: t('organic.fundingSources.subtitle'),
      questionId: EddEmbeddedFields.EDD_FUNDING_SOURCES_SELECTED,
      answer: selectedOptions,
    });

    // Save each selected option's details
    optionsList.forEach((option) => {
      if (selectedOptions.includes(option.value)) {
        // Use proper type casting to known keys instead of 'any'
        const fieldValue = JSON.stringify(
          formMethods.getValues(option.field as keyof typeof formDefaultValues)
        );
        saveEddQuestionToLocalStore({
          question: option.getLabel(),
          questionId: option.field,
          answer: fieldValue,
        });
      }
    });

    // Submit to server
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_FUNDING_SOURCES);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
    optionsList,
  };
};
